import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { loadRequestQueue } from './api';

const queueToastStorageKey = 'hyosung_queue_pending_toast';


function formatDate(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('ru-RU');
}


function normalizeText(value) {
    return String(value || '').trim().toLocaleLowerCase('ru-RU');
}


function buildCompositionItems(item) {
    if (item?.request_kind === 'cassettes') {
        return [
            { label: 'CIT 5X', value: item.cit5x ?? 0 },
            { label: 'CIT 2X', value: item.cit2x ?? 0 },
        ].filter((entry) => entry.value > 0);
    }

    return [
        { label: '5X', value: item?.csm5x ?? 0 },
        { label: '2X', value: item?.csm2x ?? 0 },
        { label: 'TE', value: item?.csmTE2x ?? 0 },
    ].filter((entry) => entry.value > 0);
}


export default function RequestQueueTablePage({
    title,
    queueName,
    summaryLabel,
    emptyQueueText,
    warningText,
    fallbackQueue,
    detailPathPrefix,
    detailButtonLabel = 'Детали',
}) {
    const navigate = useNavigate();
    const [queueState, setQueueState] = useState({
        items: [],
        loading: true,
        warning: '',
    });
    const [search, setSearch] = useState('');
    const [toast, setToast] = useState(null);

    useEffect(() => {
        try {
            const raw = window.sessionStorage.getItem(queueToastStorageKey);
            if (!raw) {
                return undefined;
            }
            const parsed = JSON.parse(raw);
            if (parsed?.target === detailPathPrefix && parsed?.text) {
                setToast({
                    type: parsed.type || 'success',
                    text: parsed.text,
                });
                window.sessionStorage.removeItem(queueToastStorageKey);
            }
        } catch (_error) {
            window.sessionStorage.removeItem(queueToastStorageKey);
        }
        return undefined;
    }, [detailPathPrefix]);

    useEffect(() => {
        if (!toast) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            setToast(null);
        }, 2600);
        return () => window.clearTimeout(timer);
    }, [toast]);

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            try {
                const queue = await loadRequestQueue(queueName, {
                    page_size: 250,
                });
                if (!cancelled) {
                    setQueueState({
                        items: queue.items || [],
                        loading: false,
                        warning: '',
                    });
                }
            } catch (_error) {
                if (!cancelled) {
                    setQueueState({
                        items: fallbackQueue.items || [],
                        loading: false,
                        warning: warningText,
                    });
                }
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, [fallbackQueue.items, queueName, warningText]);

    const filteredItems = useMemo(() => {
        const query = normalizeText(search);
        if (!query) {
            return queueState.items;
        }

        return queueState.items.filter((item) => {
            const haystack = [
                item.application_name,
                item.slug,
                item.whom,
                item.related,
                item.transport,
                item.address,
                item.location?.city,
                item.ttn,
            ].join(' ');
            return normalizeText(haystack).includes(query);
        });
    }, [queueState.items, search]);

    const totalUnits = useMemo(
        () => filteredItems.reduce((sum, item) => sum + (item.unit_count ?? (item.csm5x ?? 0) + (item.csm2x ?? 0) + (item.csmTE2x ?? 0)), 0),
        [filteredItems],
    );

    const locationsCount = useMemo(
        () => new Set(filteredItems.map((item) => item.location?.city).filter(Boolean)).size,
        [filteredItems],
    );

    const summaryCards = [
        { label: summaryLabel, value: queueState.loading ? '...' : filteredItems.length },
        { label: 'Единиц в выборке', value: queueState.loading ? '...' : totalUnits },
        { label: 'Локаций', value: queueState.loading ? '...' : locationsCount || 0 },
    ];

    return (
        <div className="shell-page">
            {toast ? (
                <div className="shell-toastStack">
                    <div className={`shell-toast ${toast.type === 'error' ? 'shell-toast--error' : ''}`}>
                        {toast.text}
                    </div>
                </div>
            ) : null}
            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact">
                    <div className="shell-pageBandIntro">
                        <h3>{title}</h3>
                    </div>

                    <div className="shell-pageSummary shell-pageSummary--queueTop shell-pageSummary--header">
                        {summaryCards.map((item) => (
                            <div key={item.label} className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>{item.label}</span>
                                <strong>{item.value}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {queueState.warning ? <div className="shell-banner">{queueState.warning}</div> : null}

            <section className="shell-section">
                <div className="shell-sectionHeader">
                    <div>
                        <h3>Список заявок</h3>
                    </div>
                    <span>{queueState.loading ? 'Загрузка...' : `${filteredItems.length} шт.`}</span>
                </div>

                <div className="shell-toolbarRow">
                    <label className="shell-field shell-field--toolbar">
                        <span>Поиск по заявкам</span>
                        <input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Название, slug, получатель, локация, ТТН"
                        />
                    </label>
                </div>

                {queueState.loading ? (
                    <div className="shell-emptyState shell-emptyState--compact">Загружаем список заявок...</div>
                ) : filteredItems.length ? (
                    <div className="shell-tableWrap">
                        <table className="shell-table">
                            <thead>
                                <tr>
                                    <th>Заявка</th>
                                    <th>Локация</th>
                                    <th>Получатель</th>
                                    <th>План / дата</th>
                                    <th>Состав</th>
                                    <th>Транспорт</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((item) => (
                                    <tr key={item.slug} className="shell-tableRow">
                                        <td>
                                            <strong>{item.application_name || item.slug}</strong>
                                            <div className="shell-tableHint">{item.slug}</div>
                                        </td>
                                        <td>{item.location?.city || '—'}</td>
                                        <td>{item.whom || item.related || '—'}</td>
                                        <td>
                                            <strong>{formatDate(item.estimated_date_of_shipment || item.application_sending)}</strong>
                                            <div className="shell-tableHint">
                                                {item.estimated_date_of_shipment ? 'План отгрузки' : 'Дата заявки'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="shell-pillRow shell-pillRow--compact">
                                                {buildCompositionItems(item).length ? buildCompositionItems(item).map((entry) => (
                                                    <span key={entry.label} className="shell-pillTag shell-pillTag--good">
                                                        {entry.label}: {entry.value}
                                                    </span>
                                                )) : (
                                                    <span className="shell-tableHint">
                                                        {item.request_kind === 'cassettes' ? 'Кассеты без указанных типов' : 'Состав не указан'}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>{item.transport || (item.request_kind === 'cassettes' ? 'Кассетная заявка' : '—')}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="shell-actionButton shell-actionButton--primary shell-actionButton--small"
                                                onClick={() => navigate(`${detailPathPrefix}/${item.slug}`)}
                                            >
                                                {detailButtonLabel}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="shell-emptyState shell-emptyState--compact">{emptyQueueText}</div>
                )}
            </section>
        </div>
    );
}
